import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Instagram, Menu, X } from "lucide-react";
import { ASSETS, SITE } from "@/constants/site";

const LINKS = [
  { label: "Supper Club", to: "/supper-club", testid: "nav-link-supper-club" },
  { label: "Catering", to: "/catering", testid: "nav-link-catering" },
  { label: "Meal Prep", to: "/meal-prep", testid: "nav-link-meal-prep" },
  { label: "Private Events & Chef Services", to: "/private-events", testid: "nav-link-private-events" },
  { label: "About", to: "/about", testid: "nav-link-about" },
  { label: "Inquiries", to: "/inquiries", testid: "nav-link-inquiries" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <>
    <header
      data-testid="site-nav"
      className="sticky top-0 z-50 border-b border-forest/10 bg-cream/90 backdrop-blur-md"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3 md:px-10">
        <Link to="/" data-testid="nav-logo-link" className="flex items-center" aria-label="Sita — home">
          <img src={ASSETS.sitaLogo} alt="Sita — earth found, home cooked" className="h-12 w-12 md:h-14 md:w-14" />
        </Link>

        <nav className="hidden items-center gap-7 lg:flex">
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className={`link-underline text-[13px] font-medium tracking-wide transition-colors ${
                pathname === l.to ? "text-terracotta" : "text-forest hover:text-terracotta"
              }`}
            >
              {l.label}
            </Link>
          ))}
          <a
            href={SITE.instagramUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-instagram-link"
            aria-label="Sita on Instagram"
            className="text-forest transition-colors hover:text-terracotta"
          >
            <Instagram size={18} strokeWidth={1.75} />
          </a>
          <Link
            to="/inquiries"
            data-testid="nav-inquire-btn"
            className="inline-flex items-center rounded-full bg-terracotta px-6 py-2.5 text-sm font-medium tracking-wide text-cream transition-[background-color,color,transform] duration-300 hover:-translate-y-0.5 hover:bg-gold hover:text-deepforest"
          >
            Inquire
          </Link>
        </nav>

        <div className="flex items-center gap-3 lg:hidden">
          <a
            href={SITE.instagramUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-instagram-link-mobile"
            aria-label="Sita on Instagram"
            className="text-forest"
          >
            <Instagram size={20} strokeWidth={1.75} />
          </a>
          <button
            data-testid="mobile-menu-button"
            aria-label={open ? "Close menu" : "Open menu"}
            onClick={() => setOpen((v) => !v)}
            className="rounded-full border border-forest/20 p-2 text-forest"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>
    </header>

    <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-x-0 top-[64px] bottom-0 z-40 flex flex-col justify-between bg-deepforest px-8 pb-10 pt-12 text-cream lg:hidden"
          >
            <nav className="flex flex-col gap-7">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.5 }}
                >
                  <Link
                    to={l.to}
                    data-testid={`${l.testid}-mobile`}
                    className="font-display text-3xl italic text-cream"
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
            </nav>
            <p className="font-display text-sm italic text-cream/60">{SITE.tagline}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
